import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Descope } from '@descope/react-sdk';
import { BrandMark } from '@/components/ui/BrandMark';
import heroImage from '@/assets/login-hero.svg';

/**
 * Login gate shown before the app. Two-column split (Figma "Sign in"):
 * a dark space hero on the left, and a light panel on the right that frames
 * Descope's hosted `sign-in` flow (Google, email/password, forgot-password —
 * all configured/styled in the Descope console). Below `md` the hero is hidden
 * and the form panel takes the full width.
 *
 * The same frame serves `/connect/slack`: a different flow, heading and
 * `onSuccess`, a `mark` above the heading (the Slack logo — says at a glance which
 * product is being connected), and `children` to show the outcome in place of the
 * flow. With no props it is the sign-in screen, unchanged.
 */
export function LoginPage({
  flowId = 'sign-in-visionheight',
  onSuccess,
  title = 'Welcome to Elliot',
  subtitle = 'Your AI partner for cybersecurity investigations',
  mark,
  children,
}: {
  flowId?: string;
  onSuccess?: () => void;
  title?: string;
  subtitle?: string;
  mark?: ReactNode;
  children?: ReactNode;
} = {}) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left: space hero (Figma export, logo layer removed). */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          flex: 1,
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Right: form panel. */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Box sx={{ position: 'absolute', top: 40, left: 40 }}>
          <BrandMark orientation="horizontal" logoSize={20} />
        </Box>


        <Stack spacing={3} sx={{ width: '100%', maxWidth: 400 }}>
          <Stack spacing={0.5} alignItems="center">
            {mark}
            <Typography variant="h4" sx={{ fontWeight: 600, textAlign: 'center' }}>
              {title}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              {subtitle}
            </Typography>
          </Stack>

          {children ?? (
            <Descope
              flowId={flowId}
              // href, not origin: a shared link like /chat/<id> opened while signed out
              // must come back to THAT url after the OAuth round-trip, not to the home
              // screen. Needs the Descope project to allow URLs carrying a path.
              redirectUrl={window.location.href}
              theme={theme.palette.mode}
              onSuccess={onSuccess}
              onError={(e) => console.error('[descope]', e.detail)}
            />
          )}
        </Stack>

        <Typography
          variant="body2"
          sx={{ position: 'absolute', bottom: 40, left: 40, color: 'text.secondary' }}
        >
          2026 VisionHeight. All rights reserved
        </Typography>
      </Box>
    </Box>
  );
}
